import {
  LoadingMessageKeys,
  PropertyShowLoadingActions,
  PropertyShowLoadingStates,
  STAGE_PROGRESS,
  defaultInitialState,
} from './constants';

/**
 * State machine reducer for the PropertyShow loading pipeline.
 * This reducer manages the state transitions and context updates for the
 * multi-stage property data fetching process. It is intentionally side-effect
 * free to simplify testing and reasoning about state changes.
 * @param {object} state - Current state of the machine
 * @param {object} action - Action to be performed
 * @param {string} action.type - Type of action to perform
 * @param {number} [action.metersCount] - Meter count for SET_METERS_LOADED
 * @param {number} [action.rulesCount] - Rules count for SET_RULES_LOADED
 * @param {number} [action.batchesLoaded] - Batches loaded for SET_TRADE_SUMMARY_PROGRESS
 * @param {number} [action.batchesTotal] - Total batches for SET_TRADE_SUMMARY_PROGRESS
 * @param {Error} [action.error] - Error for SET_ERROR
 * @returns {object} New state after applying the action
 */
const loadingStateReducer = (state, action) => {
  switch (action.type) {
    case PropertyShowLoadingActions.RESET:
      return defaultInitialState();

    case PropertyShowLoadingActions.START_REQUEST:
      return {
        state: PropertyShowLoadingStates.LOADING_PROPERTY,
        metadata: { ...defaultInitialState().metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.LOADING_PROPERTY],
          messageKey: LoadingMessageKeys.LOADING_PROPERTY,
        },
      };

    case PropertyShowLoadingActions.SET_PROPERTY_LOADED:
      return {
        state: PropertyShowLoadingStates.LOADING_METERS,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.LOADING_METERS],
          messageKey: LoadingMessageKeys.PROPERTY_LOADED,
        },
      };

    case PropertyShowLoadingActions.SET_METERS_LOADED:
      return {
        state: PropertyShowLoadingStates.METERS_LOADED,
        metadata: {
          ...state.metadata,
          metersCount: action.metersCount ?? state.metadata.metersCount,
        },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.METERS_LOADED],
          messageKey: LoadingMessageKeys.METERS_LOADED,
        },
      };

    case PropertyShowLoadingActions.METERS_DISPLAYED:
      return {
        state: PropertyShowLoadingStates.LOADING_RULES,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.LOADING_RULES],
          messageKey: LoadingMessageKeys.METERS_LOADED,
        },
      };

    case PropertyShowLoadingActions.SET_RULES_LOADED:
      return {
        state: PropertyShowLoadingStates.RULES_LOADED,
        metadata: {
          ...state.metadata,
          rulesCount: action.rulesCount ?? state.metadata.rulesCount,
        },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.RULES_LOADED],
          messageKey: LoadingMessageKeys.RULES_LOADED,
        },
      };

    case PropertyShowLoadingActions.RULES_DISPLAYED:
      return {
        state: PropertyShowLoadingStates.LOADING_CONSUMED,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.LOADING_CONSUMED],
          messageKey: LoadingMessageKeys.RULES_LOADED,
        },
      };

    case PropertyShowLoadingActions.SET_CONSUMED_DATA_LOADED:
      return {
        state: PropertyShowLoadingStates.LOADING_GENERATED,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.LOADING_GENERATED],
          messageKey: LoadingMessageKeys.CONSUMED_DATA_LOADED,
        },
      };

    case PropertyShowLoadingActions.SET_GENERATED_DATA_LOADED:
      return {
        state: PropertyShowLoadingStates.LOADING_TRADE_SUMMARY,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.LOADING_TRADE_SUMMARY],
          messageKey: LoadingMessageKeys.TRADE_SUMMARY_PROGRESS,
        },
      };

    case PropertyShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS:
      return {
        ...state,
        metadata: {
          ...state.metadata,
          batchesLoaded: action.batchesLoaded ?? state.metadata.batchesLoaded,
          batchesTotal: action.batchesTotal ?? state.metadata.batchesTotal,
        },
        overlay: {
          ...state.overlay,
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.LOADING_TRADE_SUMMARY],
          messageKey: LoadingMessageKeys.TRADE_SUMMARY_PROGRESS,
        },
      };

    case PropertyShowLoadingActions.MARK_FULLY_LOADED:
      return {
        state: PropertyShowLoadingStates.PREPARING_DATA,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.PREPARING_DATA],
          messageKey: LoadingMessageKeys.PREPARING_DATA,
        },
      };

    case PropertyShowLoadingActions.DATA_PREPARED:
      if (state.state !== PropertyShowLoadingStates.PREPARING_DATA) {
        return state;
      }
      return {
        state: PropertyShowLoadingStates.RENDERING,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.RENDERING],
          messageKey: LoadingMessageKeys.RENDERING,
        },
      };

    case PropertyShowLoadingActions.RENDERING_DISPLAYED:
      if (state.state !== PropertyShowLoadingStates.RENDERING) {
        return state;
      }
      return {
        state: PropertyShowLoadingStates.COMPLETE,
        metadata: { ...state.metadata },
        overlay: {
          progressBarValue: STAGE_PROGRESS[PropertyShowLoadingStates.COMPLETE],
          messageKey: LoadingMessageKeys.COMPLETE,
        },
      };

    case PropertyShowLoadingActions.HIDE_OVERLAY:
      if (state.state === PropertyShowLoadingStates.ERROR) {
        return state;
      }
      return {
        ...state,
        state: PropertyShowLoadingStates.IDLE,
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    case PropertyShowLoadingActions.SET_ERROR:
      return {
        state: PropertyShowLoadingStates.ERROR,
        metadata: {
          ...state.metadata,
          error: action.error || null,
        },
        overlay: {
          progressBarValue: 0,
          messageKey: LoadingMessageKeys.NONE,
        },
      };

    default:
      return state;
  }
};

export default loadingStateReducer;
